import useAuthStore from '../stores/authStore';
import useSubscriptionStore from '../stores/subscriptionStore';
import API_ENDPOINTS from '../constants/apiEndpoints';
import { API_BASE_URL } from '../constants/apiBase';
// Every request below goes through apiFetch, which adds X-CSRF-Token to the
// state-changing ones. These used to call the global fetch directly, so logout,
// login, token refresh, Etsy refresh/revoke and every non-GET
// makeAuthenticatedRequest (the timezone PATCH on each page load) 403'd at the
// backend's CSRF middleware whenever the browser sent the session cookie.
import { apiFetch } from './apiFetch';
import { clearAllRecoveryDrafts } from '../hooks/useRecoveryDraft';

class AuthService {
  constructor() {
    // Singleton refresh state — shared across all callers (useApi, useSessionMonitor, etc.)
    // so that concurrent refresh attempts reuse the same in-flight promise.
    this._isRefreshing = false;
    this._refreshPromise = null;
    // Singleton verify-connection state. 28 components currently call
    // useAuth() and each one mounts its own verify effect on auth state
    // change. Without this dedup, page loads fan out to 4–7 parallel
    // /third-party/verify-connection calls — each of which can fan out
    // again to /users/me on the Etsy side. Sharing the in-flight promise
    // collapses N callers into one round-trip.
    this._verifyInFlight = null;
  }

  // User Authentication Methods
  async loginUser(email, password, turnstileToken = null) {
    const { setUserLoading, setUserError, setUserAuth } = useAuthStore.getState();

    try {
      setUserLoading(true);
      setUserError(null);

      const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
      if (turnstileToken) {
        headers['X-Turnstile-Token'] = turnstileToken;
      }

      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.TOKEN}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: new URLSearchParams({
          username: email,
          password: password,
        }),
        signal: AbortSignal.timeout(15000), // 15-second timeout
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail;
        // Handle structured error responses
        if (detail && typeof detail === 'object') {
          const errorCode = detail.error_code;
          if (errorCode === 'AUTH.EMAIL_NOT_VERIFIED') {
            const err = new Error(detail.message || 'Please verify your email before logging in.');
            err.errorCode = errorCode;
            err.email = email;
            throw err;
          }
          if (errorCode === 'AUTH.ACCOUNT_LOCKED') {
            throw new Error(detail.message || 'Account temporarily locked. Try again later.');
          }
          throw new Error(detail.message || 'Invalid email or password');
        }
        throw new Error(detail || 'Invalid email or password');
      }

      const data = await response.json();

      // Check if the response includes user data (new format)
      if (data.user) {
        // New format includes user data in the auth response
        setUserAuth(data.user, data.access_token);
      } else {
        // Fallback: get user info from /users/me endpoint
        const userResponse = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.USERS_ME}`, {
          headers: { Authorization: `Bearer ${data.access_token}` },
          credentials: 'include',
          signal: AbortSignal.timeout(10000),
        });

        if (!userResponse.ok) {
          throw new Error('Failed to get user information');
        }

        const userData = await userResponse.json();
        setUserAuth(userData, data.access_token);
      }

      return {
        success: true,
      };
    } catch (error) {
      const message =
        error.name === 'TimeoutError' || error.name === 'AbortError'
          ? 'Login timed out — please check your connection and try again'
          : error.message;
      setUserError(message);
      return { success: false, error: message, errorCode: error.errorCode, email: error.email };
    } finally {
      setUserLoading(false);
    }
  }

  async registerUser(registrationData) {
    const { setUserLoading, setUserError } = useAuthStore.getState();

    try {
      setUserLoading(true);
      setUserError(null);

      // Support both old format (for backward compatibility) and new format
      const payload =
        typeof registrationData === 'object' && registrationData.email
          ? registrationData
          : {
              email: arguments[0],
              password: arguments[1],
              shop_name: arguments[2],
            };

      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.REGISTER}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const detail = errorData.detail;
        if (detail && typeof detail === 'object') {
          throw new Error(detail.message || 'Registration failed');
        }
        throw new Error(detail || 'Registration failed');
      }

      const data = await response.json();

      // Check if email verification is required
      if (data.user && !data.user.email_verified) {
        return { success: true, emailVerificationRequired: true, email: payload.email };
      }

      // Check if the response includes user data (new format)
      if (data.user && data.access_token) {
        // New format includes user data in the registration response
        const { setUserAuth } = useAuthStore.getState();
        setUserAuth(data.user, data.access_token);
        return { success: true };
      } else {
        // Fallback: log them in to get user data
        const email = payload.email;
        const password = payload.password;
        return await this.loginUser(email, password);
      }
    } catch (error) {
      setUserError(error.message);
      return { success: false, error: error.message };
    } finally {
      setUserLoading(false);
    }
  }

  async resendVerificationEmail(email) {
    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.RESEND_VERIFICATION}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });
      return { success: response.ok };
    } catch {
      return { success: false };
    }
  }

  async refreshUserData() {
    const { userToken, setUserAuth, setUserError } = useAuthStore.getState();

    if (!userToken) {
      return { success: false, error: 'No token available' };
    }

    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.USERS_ME}`, {
        headers: { Authorization: `Bearer ${userToken}` },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to refresh user data');
      }

      const userData = await response.json();
      setUserAuth(userData, userToken);

      return { success: true, user: userData };
    } catch (error) {
      setUserError(error.message);
      return { success: false, error: error.message };
    }
  }

  async refreshJwtToken() {
    // Return the in-flight promise if a refresh is already running.
    // This prevents duplicate refresh calls from useApi, useSessionMonitor, etc.
    if (this._isRefreshing && this._refreshPromise) {
      return this._refreshPromise;
    }

    const { userToken } = useAuthStore.getState();
    if (!userToken) {
      return { success: false, error: 'No token available' };
    }

    this._isRefreshing = true;
    this._refreshPromise = this._executeRefresh(userToken);
    return this._refreshPromise;
  }

  async _executeRefresh(userToken) {
    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH_TOKEN}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${userToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        signal: AbortSignal.timeout(10000), // 10-second timeout to prevent hanging
      });

      if (!response.ok) {
        const errorText = await response.text();
        // 429 means another tab likely already refreshed and hit the rate limit.
        // Return a typed failure so callers can distinguish this from a real auth
        // error and avoid logging the user out unnecessarily.
        if (response.status === 429) {
          return { success: false, error: '429 rate limited — another tab may have refreshed' };
        }
        throw new Error(`Token refresh failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (data.access_token && data.user) {
        const { setUserAuth } = useAuthStore.getState();
        setUserAuth(data.user, data.access_token);
        return {
          success: true,
          access_token: data.access_token,
          user: data.user,
        };
      } else {
        throw new Error('Invalid refresh response');
      }
    } catch (error) {
      console.error('❌ JWT token refresh failed:', error);
      const { setUserError } = useAuthStore.getState();
      setUserError(error.message);
      // Don't automatically log out on refresh failure - let the caller decide
      return { success: false, error: error.message };
    } finally {
      this._isRefreshing = false;
      this._refreshPromise = null;
    }
  }

  async logoutUser() {
    // Clear server-side cookies
    try {
      await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.LOGOUT}`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Best-effort — still clear local state even if request fails
    }
    const { clearUserAuth, clearEtsyAuth } = useAuthStore.getState();
    clearAllRecoveryDrafts(); // Unsaved invoice copies hold customer details: they do not outlive the session
    clearUserAuth();
    clearEtsyAuth(); // Also clear Etsy connection when user logs out
    useSubscriptionStore.getState().resetStore(); // Clear subscription state to prevent cross-account data leaks
  }

  // Etsy Authentication Methods
  async checkEtsyConnection() {
    // Coalesce concurrent calls. If a verify is already in flight, every
    // subsequent caller (other mounted components firing their useAuth
    // effect at the same time) awaits the same promise instead of starting
    // a fresh round-trip. Reset to null in the finally clause so the next
    // legitimately new verify can fire.
    if (this._verifyInFlight) {
      return this._verifyInFlight;
    }
    this._verifyInFlight = this._checkEtsyConnectionImpl().finally(() => {
      this._verifyInFlight = null;
    });
    return this._verifyInFlight;
  }

  /**
   * DB-only connection state from the backend. Costs no Etsy quota.
   *
   * This is the ONLY thing that can tell "your grant was revoked N days ago"
   * apart from "you never connected" — the two used to be indistinguishable
   * both here and on the server, which is how a dead OAuth stayed invisible
   * for 58 days. Returns the payload, or null when it could not be fetched
   * (never throws; an unreachable status endpoint must not blank the UI).
   */
  async fetchEtsyConnectionStatus() {
    const { userToken, isUserAuthenticated, setEtsyConnectionState } = useAuthStore.getState();
    if (!isUserAuthenticated || !userToken) return null;

    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.THIRD_PARTY.CONNECTION_STATUS}`, {
        headers: { Authorization: `Bearer ${userToken}` },
        credentials: 'include',
      });
      if (!response.ok) return null;
      const data = await response.json();
      setEtsyConnectionState(data);
      return data;
    } catch (error) {
      console.error('Etsy connection status check failed:', error);
      return null;
    }
  }

  async _checkEtsyConnectionImpl() {
    const {
      userToken,
      isUserAuthenticated,
      setEtsyLoading,
      setEtsyError,
      setEtsyConnection,
      clearEtsyAuth,
      isEtsyTokenValid,
      etsyTokens,
    } = useAuthStore.getState();

    try {
      setEtsyLoading(true);
      setEtsyError(null);

      // ASK THE SERVER FIRST. This used to short-circuit on empty localStorage
      // and return false without issuing a single request — so a fresh browser
      // and a seller whose grant Etsy revoked produced identical UI, and the
      // one who needed to act was never told. The status endpoint is two local
      // DB queries; it burns none of the 5,000 QPD app-wide budget.
      const status = await this.fetchEtsyConnectionStatus();
      if (status && status.requires_reconnect) {
        // Reconnecting is the only fix — verifying against Etsy is a
        // guaranteed-failing call we would pay app-wide quota for.
        return false;
      }

      // No tokens at all — genuinely disconnected, and the server agrees.
      if (!etsyTokens.accessToken && !etsyTokens.refreshToken) {
        clearEtsyAuth();
        return false;
      }

      // Expired (or expiring) access token: refresh with the 90-day refresh
      // token instead of clearing — clearing here forced a full re-OAuth on
      // every page load >55 min after connect. getValidEtsyToken refreshes,
      // and clears auth itself only when the refresh definitively fails.
      if (!etsyTokens.accessToken || !isEtsyTokenValid()) {
        const refreshed = await useAuthStore.getState().getValidEtsyToken();
        if (!refreshed) {
          return false;
        }
      }

      // If user is not logged in to our app, we can't verify with backend
      // but we can still return true if we have valid Etsy tokens
      if (!isUserAuthenticated || !userToken) {
        // Don't clear auth, just return false for backend verification
        return false;
      }

      // Verify connection with backend

      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.THIRD_PARTY.VERIFY_CONNECTION}`, {
        headers: { Authorization: `Bearer ${userToken}` },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Connection verification failed');
      }

      const data = await response.json();

      // Rate-limited path: backend couldn't verify against Etsy because
      // /users/me returned 429. The token is still valid; we just can't
      // re-confirm it right now. The backend may have substituted a
      // last-known-good payload (data.stale === true), in which case
      // user_info / shop_info are present and trustworthy short-term.
      // Either way: do NOT clear auth — clearing here would push the
      // seller into a reconnect flow that won't work (the new OAuth
      // round-trip would also be rate-limited) and would lose the LKG
      // shop info on the next render.
      if (data.rate_limited) {
        const { setEtsyRateLimited } = useAuthStore.getState();
        setEtsyRateLimited({
          retryAfter: typeof data.retry_after === 'number' ? data.retry_after : null,
          stale: !!data.stale,
          userInfo: data.user_info,
          shopInfo: data.shop_info,
        });
        return true;
      }

      if (data.connected) {
        setEtsyConnection(data.user_info, data.shop_info);
        return true;
      } else {
        clearEtsyAuth();
        return false;
      }
    } catch (error) {
      // Do NOT clearEtsyAuth() here. A THROWN error means the check itself
      // failed — a timeout, a 500, a dropped connection — which says nothing
      // about whether the shop is connected. Clearing on it meant one flaky
      // request blanked every Etsy page, and the state stuck until the next
      // successful verify.
      //
      // The server answering `connected: false` is the only thing that means
      // disconnected, and that branch above still clears.
      console.error('Etsy connection check failed:', error);
      setEtsyError(error.message);
      return useAuthStore.getState().isEtsyConnected;
    } finally {
      setEtsyLoading(false);
    }
  }

  async handleEtsyOAuthCallback(code, state) {
    const { setEtsyLoading, setEtsyError, setEtsyTokens, etsyTokens } = useAuthStore.getState();

    // Add check for existing tokens to prevent duplicate processing
    if (etsyTokens.accessToken) {
      return { success: true };
    }

    try {
      setEtsyLoading(true);
      setEtsyError(null);

      // Add user token to request if available
      const { userToken } = useAuthStore.getState();
      const headers = userToken ? { Authorization: `Bearer ${userToken}` } : {};

      const url = `${API_BASE_URL}${API_ENDPOINTS.THIRD_PARTY.OAUTH_REDIRECT}?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;

      const response = await apiFetch(url, {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response error:', errorText);
        throw new Error(`OAuth callback failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (data.success && data.access_token) {
        // Store tokens in sessionStorage to prevent loss on re-renders
        sessionStorage.setItem('etsy_oauth_completed', 'true');

        setEtsyTokens(data);

        if (data.shop_info) {
          // The OAuth exchange already proved the connection and returned the
          // shop identity — mark connected directly instead of a follow-up
          // verify-connection (3 more Etsy calls behind the spinner). The
          // backend seeded its verify cache, so later verifies stay cheap.
          const { setEtsyConnection } = useAuthStore.getState();
          setEtsyConnection(data.etsy_user_id ? { user_id: data.etsy_user_id } : null, data.shop_info);
        } else {
          // Legacy/fallback: shop lookup failed during OAuth — verify the
          // connection the old way (only if user is logged in).
          const { isUserAuthenticated } = useAuthStore.getState();
          if (isUserAuthenticated && etsyTokens) {
            await this.checkEtsyConnection();
          }
        }

        return { success: true };
      } else {
        throw new Error(data.message || 'OAuth failed');
      }
    } catch (error) {
      console.error('💥 handleEtsyOAuthCallback error:', error);
      setEtsyError(error.message);
      return { success: false, error: error.message };
    } finally {
      setEtsyLoading(false);
    }
  }

  async refreshEtsyToken(refreshToken) {
    const { userToken } = useAuthStore.getState();

    try {
      const headers = {
        'Content-Type': 'application/json',
        ...(userToken && { Authorization: `Bearer ${userToken}` }),
      };

      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.THIRD_PARTY.REFRESH_TOKEN}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Token refresh failed:', errorText);
        throw new Error(`Token refresh failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (data.success && data.access_token) {
        return {
          success: true,
          access_token: data.access_token,
          refresh_token: data.refresh_token || refreshToken, // Use new refresh token if provided
          expires_in: data.expires_in,
          expires_at: data.expires_at,
          token_type: data.token_type || 'Bearer',
        };
      } else {
        throw new Error(data.message || 'Token refresh failed');
      }
    } catch (error) {
      console.error('💥 AuthService: refreshEtsyToken error:', error);
      return {
        success: false,
        error: error.message || 'Token refresh failed',
      };
    }
  }

  async disconnectEtsy() {
    const { userToken, setEtsyLoading, setEtsyError, clearEtsyAuth } = useAuthStore.getState();

    try {
      setEtsyLoading(true);
      setEtsyError(null);

      // Try to revoke on backend (optional)
      if (userToken) {
        try {
          await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.THIRD_PARTY.REVOKE_TOKEN}`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${userToken}` },
            credentials: 'include',
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Failed to revoke token on backend:', error);
        }
      }

      clearEtsyAuth();
      // The user chose this. clearEtsyAuth only drops local credentials, so
      // say explicitly what the connection now IS — otherwise a stale
      // 'connected' (or a resolved 'reconnect_required') would survive the
      // disconnect and keep its banner on screen.
      useAuthStore.getState().setEtsyConnectionState({ state: 'never_connected', connected: false });
      return { success: true };
    } catch (error) {
      setEtsyError(error.message);
      return { success: false, error: error.message };
    } finally {
      setEtsyLoading(false);
    }
  }

  // API Helper Methods
  async makeAuthenticatedRequest(endpoint, options = {}) {
    const { userToken } = useAuthStore.getState();

    if (!userToken) {
      throw new Error('User not authenticated');
    }

    // NO client-side Etsy token gate here.
    //
    // This used to treat any endpoint containing '/orders/' as "needs an Etsy
    // token", call getValidEtsyToken(), and THROW 'Etsy authentication required'
    // BEFORE issuing the request. That was the last thing blanking the Etsy admin
    // pages — the backend serves orders DB-first from `current_user`, so the
    // request would have succeeded. It was simply never sent.
    //
    // Two independent reasons it was wrong, not just badly placed:
    //
    //   1. No route accepts a client-supplied Etsy token; the backend resolves it
    //      from the DB. The injected `?access_token=` was read by nothing, and
    //      putting an OAuth token in a query string leaks it into access logs,
    //      proxies and Referer headers.
    //   2. `includes('/orders/')` is a raw substring match, so it also caught
    //      /shopify/orders/*, /ecommerce/admin/orders/* — CraftFlow's own
    //      storefront orders, packing slips, batch labels — and every local
    //      print/pack status endpoint. A seller with no Etsy connection could not
    //      reach their Shopify or CraftFlow orders because of an Etsy check.
    //
    // Real Etsy-auth failures are reported by the server and handled on the 401
    // path below.
    const finalEndpoint = endpoint;

    // Attempt the request with current token. apiFetch re-reads the csrf_token
    // cookie on each call, so the post-refresh retry below sends the rotated one.
    const makeRequest = async token => {
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      };

      return apiFetch(`${API_BASE_URL}${finalEndpoint}`, {
        ...options,
        headers,
        credentials: 'include',
      });
    };

    let response = await makeRequest(userToken);

    // If we get 401 and it's not an Etsy token issue, try to refresh JWT token
    if (response.status === 401) {
      const errorText = await response.text();

      // Whether a 401 is an Etsy problem is decided by what the SERVER said, not
      // by the shape of the URL. The old `|| needsEtsyToken` term made every
      // '/orders/' 401 an "Etsy session expired" — so an expired CraftFlow JWT on
      // the orders page told you to reconnect Etsy and never refreshed the JWT,
      // which is the one thing that would have fixed it.
      const isEtsyTokenIssue = errorText.includes('etsy') || errorText.includes('Etsy');
      const isJwtExpiredIssue = errorText.includes('Signature has expired') || errorText.includes('expired');

      if (!isEtsyTokenIssue && (isJwtExpiredIssue || errorText.includes('JWT'))) {
        try {
          const refreshResult = await this.refreshJwtToken();
          if (refreshResult.success) {
            // Retry the request with the new token
            response = await makeRequest(refreshResult.access_token);
          } else {
            this.logoutUser();
            throw new Error('Session expired. Please log in again.');
          }
        } catch (refreshError) {
          console.error('❌ JWT token refresh error:', refreshError);
          this.logoutUser();
          throw new Error('Session expired. Please log in again.');
        }
      } else if (isEtsyTokenIssue) {
        // This is an Etsy token issue
        throw new Error('Etsy session expired. Please reconnect your Etsy account.');
      } else {
        // Other authentication error
        this.logoutUser();
        throw new Error('Session expired. Please log in again.');
      }
    }

    if (!response.ok) {
      const errorText = await response.text();
      // Only log non-503 errors (503s are transient Etsy API timeouts)
      if (response.status !== 503) {
        console.error('❌ API Request failed:', {
          endpoint: finalEndpoint,
          status: response.status,
          statusText: response.statusText,
          errorText: errorText,
        });
      }

      throw new Error(`Request failed: ${response.status} - ${errorText}`);
    }

    return response.json();
  }
}

const authServiceInstance = new AuthService();
export default authServiceInstance;
